import { Box, Button, Link, makeStyles, Snackbar } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { MouseEventHandler } from "react";
import { useRecoilValue } from "recoil";
import LogToSentry, { SentryErrorLevels } from "../SentryLog";
import { userPersonalInfoState } from "../../store/user";
import { cyrb53 } from "../../utils/hash";
import { Mutation, Query } from "../../models/generated/graphql";

const useStyles = makeStyles(() => ({
	snackbarBase: {
		width: "100%",
		bottom: 0,
		left: 0,
	},

	openSnackBar: {
		composes: "$snackbarBase",
		opacity: 1,
		transform: "translateY(0)",
		transition: "opacity 300ms ease-out, transform 300ms ease-out",
	},

	closeSnackBar: {
		composes: "$snackbarBase",
		opacity: 0,
		transform: "scale(0.95)",
		transition: "opacity 300ms ease-in-out, transform 300ms ease-in-out",
	},
	outerFlex: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "18px 40px",
		bottom: 0,
		left: 0,
		right: 0,
		boxShadow: "0px 0px 24px 0px rgba(50, 50, 50, 0.12)",
		position: "fixed",
		background: "#FFF",
		color: "#212121",
		zIndex: 1000,
	},
	dismissButton: {
		fontSize: "12px",
		background: "#FFFFFF ",
		color: " #367BF5",
		border: "1px solid #367BF5",
	},
	tryAgainButton: {
		fontSize: "12px",
		color: "#FFFFFF",
	},
}));

interface IErrorBannerProps {
	errorTitle: string;
	secondaryText: string;
	isOpen: boolean;
	onClose: () => void;
	handleTryAgain: MouseEventHandler<HTMLButtonElement> | undefined;
	error?: Error | string | null;
	statusCode?: number;
	operation?: keyof Mutation | keyof Query;
}

function BottomErrorBanner(props: IErrorBannerProps) {
	const { t } = useTranslation();
	const classes = useStyles();
	const { isOpen, errorTitle, secondaryText, onClose, handleTryAgain, error, operation, statusCode } = props;
	const activeUser = useRecoilValue(userPersonalInfoState);

	return (
		<>
			{error && isOpen && (
				<LogToSentry
					level={SentryErrorLevels.Error}
					errorLog={`${errorTitle} - ${error}`}
					statusCode={statusCode}
					userId={cyrb53(activeUser.id).toString()}
					operation={operation}
				/>
			)}
			<Snackbar
				open={isOpen}
				onClose={onClose}
				className={`${classes.snackbarBase} ${props.isOpen ? classes.openSnackBar : classes.closeSnackBar}`}
			>
				<Box className={classes.outerFlex}>
					<Box justifyContent={"flex-start"} display={"flex"} alignItems={"center"}>
						<Box mr={"16px"}>
							<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
								<path
									d="M29.6 23.5113L18.6687 4.52758C18.3956 4.06249 18.0056 3.67686 17.5375 3.40891C17.0694 3.14096 16.5394 3 16 3C15.4606 3 14.9306 3.14096 14.4625 3.40891C13.9944 3.67686 13.6044 4.06249 13.3312 4.52758L2.4 23.5113C2.13717 23.9612 1.99866 24.4728 1.99866 24.9938C1.99866 25.5148 2.13717 26.0265 2.4 26.4763C2.66966 26.9442 3.05896 27.332 3.52795 27.5997C3.99694 27.8675 4.52873 28.0057 5.06875 28.0001H26.9312C27.4708 28.0052 28.0021 27.8669 28.4706 27.5991C28.9391 27.3314 29.328 26.9439 29.5975 26.4763C29.8607 26.0267 29.9997 25.5152 30.0001 24.9942C30.0005 24.4732 29.8624 23.9614 29.6 23.5113ZM27.8662 25.4751C27.771 25.6376 27.6341 25.7719 27.4698 25.8642C27.3055 25.9564 27.1197 26.0033 26.9312 26.0001H5.06875C4.88034 26.0033 4.69446 25.9564 4.53016 25.8642C4.36586 25.7719 4.22904 25.6376 4.13375 25.4751C4.04743 25.3289 4.00189 25.1623 4.00189 24.9926C4.00189 24.8228 4.04743 24.6562 4.13375 24.5101L15.065 5.52633C15.1622 5.36455 15.2997 5.23068 15.4639 5.13774C15.6282 5.04479 15.8138 4.99595 16.0025 4.99595C16.1912 4.99595 16.3768 5.04479 16.5411 5.13774C16.7053 5.23068 16.8428 5.36455 16.94 5.52633L27.8712 24.5101C27.9568 24.6567 28.0015 24.8235 28.0006 24.9933C27.9997 25.163 27.9533 25.3294 27.8662 25.4751ZM15 18.0001V13.0001C15 12.7349 15.1054 12.4805 15.2929 12.293C15.4804 12.1054 15.7348 12.0001 16 12.0001C16.2652 12.0001 16.5196 12.1054 16.7071 12.293C16.8946 12.4805 17 12.7349 17 13.0001V18.0001C17 18.2653 16.8946 18.5197 16.7071 18.7072C16.5196 18.8947 16.2652 19.0001 16 19.0001C15.7348 19.0001 15.4804 18.8947 15.2929 18.7072C15.1054 18.5197 15 18.2653 15 18.0001ZM17.5 22.5001C17.5 22.7968 17.412 23.0868 17.2472 23.3334C17.0824 23.5801 16.8481 23.7724 16.574 23.8859C16.2999 23.9994 15.9983 24.0291 15.7074 23.9713C15.4164 23.9134 15.1491 23.7705 14.9393 23.5607C14.7296 23.351 14.5867 23.0837 14.5288 22.7927C14.4709 22.5017 14.5006 22.2001 14.6142 21.9261C14.7277 21.652 14.92 21.4177 15.1666 21.2529C15.4133 21.0881 15.7033 21.0001 16 21.0001C16.3978 21.0001 16.7794 21.1581 17.0607 21.4394C17.342 21.7207 17.5 22.1023 17.5 22.5001Z"
									fill="#ED544D"
								/>
							</svg>
						</Box>
						<Box fontSize={"16px"} width={"640px"}>
							<span style={{ fontWeight: 700 }}>{errorTitle}</span>&nbsp;
							{secondaryText}&nbsp;
							<Link href="mailto:support@lyfegen.com" style={{ fontSize: "16px", fontWeight: 700 }}>
								support@lyfegen.com.
							</Link>
						</Box>
					</Box>
					<Box display={"flex"} justifyContent={"flex-end"} style={{ gap: "12px" }}>
						<Button variant="outlined" onClick={onClose} className={classes.dismissButton}>
							{t("dismiss")}
						</Button>

						<Button
							variant="outlined"
							onClick={handleTryAgain}
							className={classes.tryAgainButton}
							style={{ backgroundColor: " #367BF5" }}
						>
							{t("try-again")}
						</Button>
					</Box>
				</Box>
			</Snackbar>
		</>
	);
}

export default BottomErrorBanner;
